import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import { useKeyboardShortcuts, useToast } from '@/hooks/useKeyboardShortcuts.js';
import {
  VACACION_BY_ID,
  VACACION_CREATE,
  VACACION_UPDATE,
} from '@/graphql/rrhh/vacaciones.gql.js';

const ESTADOS = [
  { label: 'Solicitada', value: 'Solicitada' },
  { label: 'Aprobada', value: 'Aprobada' },
  { label: 'Rechazada', value: 'Rechazada' },
  { label: 'Goce', value: 'Goce' },
];

const INITIAL_STATE = {
  empleadoId: '',
  periodo: '',
  desde: '',
  hasta: '',
  diasTomados: 0,
  diasRestantes: 0,
  estado: 'Solicitada',
  aprobadorId: '',
  notas: '',
  sucursalId: '',
};

function calculateDiasTomados(desde, hasta) {
  if (!desde || !hasta) return 0;
  const start = new Date(desde);
  const end = new Date(hasta);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  const diff = Math.round((end - start) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff + 1 : 0;
}

function validate(form) {
  const errors = {};
  if (!form.empleadoId) {
    errors.empleadoId = 'Seleccioná un empleado.';
  }
  if (!form.periodo) {
    errors.periodo = 'Ingresá el periodo (YYYY).';
  }
  if (!form.desde) {
    errors.desde = 'Seleccioná la fecha de inicio.';
  }
  if (!form.hasta) {
    errors.hasta = 'Seleccioná la fecha de fin.';
  }
  if (form.desde && form.hasta && form.desde > form.hasta) {
    errors.hasta = 'La fecha de fin debe ser posterior a la de inicio.';
  }
  if (Number(form.diasTomados) <= 0) {
    errors.diasTomados = 'La solicitud debe cubrir al menos un día.';
  }
  return errors;
}

export default function FormVacacion() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const toast = useToast();
  const formRef = useRef(null);

  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [diasDisponibles, setDiasDisponibles] = useState(null);

  const [{ data, fetching }] = useQuery({
    query: VACACION_BY_ID,
    variables: { id },
    pause: !isEdit,
  });

  const [, createVacacion] = useMutation(VACACION_CREATE);
  const [, updateVacacion] = useMutation(VACACION_UPDATE);

  useEffect(() => {
    if (data?.vacacion) {
      setForm({
        empleadoId: data.vacacion.empleadoId?.toString() || '',
        periodo: data.vacacion.periodo?.toString() || '',
        desde: data.vacacion.desde || '',
        hasta: data.vacacion.hasta || '',
        diasTomados: data.vacacion.diasTomados || 0,
        diasRestantes: data.vacacion.diasRestantes || 0,
        estado: data.vacacion.estado || 'Solicitada',
        aprobadorId: data.vacacion.aprobadorId?.toString() || '',
        notas: data.vacacion.notas || '',
        sucursalId: data.vacacion.sucursalId?.toString() || '',
      });
      setDiasDisponibles((data.vacacion.diasTomados || 0) + (data.vacacion.diasRestantes || 0));
    }
  }, [data]);

  useKeyboardShortcuts(() => {
    formRef.current?.requestSubmit();
  });

  const pageTitle = useMemo(() => (isEdit ? 'Editar solicitud de vacaciones' : 'Nueva solicitud de vacaciones'), [isEdit]);

  const breadcrumbs = useMemo(() => [
    { label: 'RRHH', href: '/rrhh' },
    { label: 'Vacaciones', href: '/rrhh/vacaciones' },
    { label: isEdit ? 'Editar' : 'Nueva' },
  ], [isEdit]);

  const updateDiasTomados = (desdeValue, hastaValue) => {
    const dias = calculateDiasTomados(desdeValue, hastaValue);
    setForm(prev => {
      const base = diasDisponibles !== null ? diasDisponibles : Number(prev.diasRestantes) + Number(prev.diasTomados);
      const restantes = Math.max(0, base - dias);
      return { ...prev, diasTomados: dias, diasRestantes: restantes };
    });
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm(prev => {
      if (field === 'desde' || field === 'hasta') {
        const next = { ...prev, [field]: value };
        // Simplificación actual: contamos días corridos. Mejora pendiente: excluir fines de semana y feriados.
        const dias = calculateDiasTomados(field === 'desde' ? value : next.desde, field === 'hasta' ? value : next.hasta);
        const base = diasDisponibles !== null ? diasDisponibles : Number(prev.diasRestantes) + Number(prev.diasTomados);
        return { ...next, diasTomados: dias, diasRestantes: Math.max(0, base - dias) };
      }
      if (field === 'diasRestantes') {
        const numberValue = Number(value) || 0;
        setDiasDisponibles(numberValue + Number(prev.diasTomados));
        return { ...prev, diasRestantes: numberValue };
      }
      if (field === 'diasTomados') {
        const numberValue = Number(value) || 0;
        const base = diasDisponibles !== null ? diasDisponibles : Number(prev.diasRestantes) + Number(prev.diasTomados);
        return { ...prev, diasTomados: numberValue, diasRestantes: Math.max(0, base - numberValue) };
      }
      if (field === 'estado') {
        return { ...prev, estado: value };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const validation = validate(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      toast.warning('Revisá los campos marcados antes de guardar.');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      empleadoId: Number(form.empleadoId),
      periodo: form.periodo,
      desde: form.desde,
      hasta: form.hasta,
      diasTomados: Number(form.diasTomados) || 0,
      diasRestantes: Number(form.diasRestantes) || 0,
      estado: form.estado,
      aprobadorId: form.aprobadorId ? Number(form.aprobadorId) : null,
      notas: form.notas || null,
      sucursalId: form.sucursalId ? Number(form.sucursalId) : null,
    };

    const result = isEdit
      ? await updateVacacion({ id, input: payload })
      : await createVacacion({ input: payload });

    setIsSubmitting(false);

    if (result.error) {
      toast.error(result.error?.message || 'No pudimos guardar la solicitud.');
      return;
    }

    toast.success(isEdit ? 'Actualizamos la solicitud.' : 'Registramos la solicitud.');
    navigate('/rrhh/vacaciones');
  };

  return (
    <div className="w-full">
      <PageHeader
        title={pageTitle}
        description="Administrá los periodos de descanso con cálculo automático de días."
        breadcrumbs={breadcrumbs}
      />

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="mx-auto grid w-full max-w-[1400px] grid-cols-12 gap-6"
      >
        <section className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.95)] p-6 shadow-[0_8px_30px_rgba(var(--shadow),0.15)]">
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Datos de la solicitud</h2>
          <div className="mt-4 grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <Field label="Empleado" htmlFor="empleadoId" required error={errors.empleadoId}>
                <input
                  id="empleadoId"
                  value={form.empleadoId}
                  onChange={handleChange('empleadoId')}
                  placeholder="ID colaborador"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-3">
              <Field label="Periodo" htmlFor="periodo" required error={errors.periodo}>
                <input
                  id="periodo"
                  value={form.periodo}
                  onChange={handleChange('periodo')}
                  placeholder="2024"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-3">
              <Field label="Sucursal" htmlFor="sucursalId" hint="Opcional">
                <input
                  id="sucursalId"
                  value={form.sucursalId}
                  onChange={handleChange('sucursalId')}
                  placeholder="ID sucursal"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Desde" htmlFor="desde" required error={errors.desde}>
                <input
                  id="desde"
                  type="date"
                  value={form.desde}
                  onChange={event => {
                    setForm(prev => ({ ...prev, desde: event.target.value }));
                    updateDiasTomados(event.target.value, form.hasta);
                  }}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Hasta" htmlFor="hasta" required error={errors.hasta}>
                <input
                  id="hasta"
                  type="date"
                  value={form.hasta}
                  onChange={event => {
                    setForm(prev => ({ ...prev, hasta: event.target.value }));
                    updateDiasTomados(form.desde, event.target.value);
                  }}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-4">
              <Field label="Días tomados" htmlFor="diasTomados" required error={errors.diasTomados}>
                <input
                  id="diasTomados"
                  type="number"
                  min="0"
                  value={form.diasTomados}
                  onChange={handleChange('diasTomados')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-4">
              <Field label="Días restantes" htmlFor="diasRestantes">
                <input
                  id="diasRestantes"
                  type="number"
                  min="0"
                  value={form.diasRestantes}
                  onChange={handleChange('diasRestantes')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-4">
              <Field label="Estado" htmlFor="estado">
                <select
                  id="estado"
                  value={form.estado}
                  onChange={handleChange('estado')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {ESTADOS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Aprobador" htmlFor="aprobadorId" hint="Opcional">
                <input
                  id="aprobadorId"
                  value={form.aprobadorId}
                  onChange={handleChange('aprobadorId')}
                  placeholder="ID de quien aprueba"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12">
              <Field label="Notas" htmlFor="notas" hint="Comentarios adicionales">
                <textarea
                  id="notas"
                  value={form.notas}
                  onChange={handleChange('notas')}
                  rows={4}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
          </div>
          {fetching ? (
            <p className="mt-4 text-sm text-[rgba(var(--muted),0.9)]">Cargando datos de la solicitud...</p>
          ) : null}
        </section>
      </form>

      <StickyActions
        onCancel={() => navigate('/rrhh/vacaciones')}
        onSubmit={() => formRef.current?.requestSubmit()}
        isSubmitting={isSubmitting}
        primaryLabel={isEdit ? 'Guardar cambios' : 'Registrar solicitud'}
      />
    </div>
  );
}
